import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type DependencyList,
  type ReactNode,
} from "react";

const CheckoutFooterStateContext = createContext<ReactNode>(null);
const CheckoutFooterDispatchContext = createContext<
  (footer: ReactNode) => void
>(() => {});

type CheckoutFooterContextValue = {
  footer: ReactNode;
  setFooter: (footer: ReactNode) => void;
};

function useCheckoutFooterContext(): CheckoutFooterContextValue {
  return {
    footer: useContext(CheckoutFooterStateContext),
    setFooter: useContext(CheckoutFooterDispatchContext),
  };
}

function useCheckoutFooter(footer: ReactNode, deps: DependencyList = []) {
  const setFooter = useContext(CheckoutFooterDispatchContext);

  useLayoutEffect(() => {
    setFooter(footer);
    return () => setFooter(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const CheckoutFooterProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [footer, setFooterState] = useState<ReactNode>(null);
  const setFooter = useCallback((nextFooter: ReactNode) => {
    setFooterState(nextFooter);
  }, []);

  return (
    <CheckoutFooterDispatchContext.Provider value={setFooter}>
      <CheckoutFooterStateContext.Provider value={footer}>
        {children}
      </CheckoutFooterStateContext.Provider>
    </CheckoutFooterDispatchContext.Provider>
  );
};

export { CheckoutFooterProvider, useCheckoutFooter, useCheckoutFooterContext };
